import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import { ThemeProvider } from './context/ThemeContext';
import { LocaleProvider } from './context/LocaleContext';
import { AuthProvider } from './components/AuthProvider';
import { registerServiceWorker } from './utils/registerSW';
import { captureAttribution } from './utils/attribution';

// Capture attribution immediately on page load (before React hydration)
// This ensures UTM params and referrer are captured even if user navigates quickly
captureAttribution();

// CLS FIX: Map routes to their lazy-loaded page chunks
// These chunks must be loaded BEFORE hydration to prevent mismatch
const ROUTE_CHUNK_MAP: Record<string, () => Promise<unknown>> = {
  // Gemini routes
  '/remove-gemini-watermarks': () => import('./pages/GeminiWatermarkPage'),
  '/de/gemini-wasserzeichen-entfernen': () => import('./pages/GeminiWatermarkPage'),
  '/pt-br/remover-marca-dagua-gemini': () => import('./pages/GeminiWatermarkPage'),
  '/my/buang-tanda-air-gemini': () => import('./pages/GeminiWatermarkPage'),
  '/es/eliminar-marca-de-agua-gemini': () => import('./pages/GeminiWatermarkPage'),
  // Claude routes
  '/remove-claude-watermarks': () => import('./pages/ClaudeWatermarkPage'),
  '/de/claude-wasserzeichen-entfernen': () => import('./pages/ClaudeWatermarkPage'),
  '/pt-br/remover-marca-dagua-claude': () => import('./pages/ClaudeWatermarkPage'),
  '/my/buang-tanda-air-claude': () => import('./pages/ClaudeWatermarkPage'),
  '/es/eliminar-marca-de-agua-claude': () => import('./pages/ClaudeWatermarkPage'),
  // ChatGPT routes
  '/remove-chatgpt-watermarks': () => import('./pages/ChatGPTWatermarkPage'),
  '/de/chatgpt-wasserzeichen-entfernen': () => import('./pages/ChatGPTWatermarkPage'),
  '/pt-br/remover-marca-dagua-chatgpt': () => import('./pages/ChatGPTWatermarkPage'),
  '/my/buang-tanda-air-chatgpt': () => import('./pages/ChatGPTWatermarkPage'),
  '/es/eliminar-marca-de-agua-chatgpt': () => import('./pages/ChatGPTWatermarkPage'),
  // Student routes
  '/ai-watermark-remover-students': () => import('./pages/StudentWatermarkPage'),
  '/de/ki-wasserzeichen-entferner-studenten': () => import('./pages/StudentWatermarkPage'),
  '/pt-br/removedor-ai-estudantes': () => import('./pages/StudentWatermarkPage'),
  '/my/penyingkir-tanda-air-ai-pelajar': () => import('./pages/StudentWatermarkPage'),
  '/es/eliminar-marca-de-agua-texto-estudiantes': () => import('./pages/StudentWatermarkPage'),
  // Free pSEO pages
  '/free-chatgpt-watermark-remover': () => import('./pages/FreeChatGPTWatermarkRemoverPage'),
  '/free-ai-text-watermark-remover': () => import('./pages/FreeAiTextWatermarkRemoverPage'),
  '/free-ai-watermark-remover': () => import('./pages/FreeAiWatermarkRemoverPage'),
  '/free-gpt-watermark-remover': () => import('./pages/FreeGPTWatermarkRemoverPage'),
  // API pages (all locales)
  '/api': () => import('./pages/api/ApiPage'),
  '/de/api-schnittstelle': () => import('./pages/api/ApiPage'),
  '/pt-br/api-interface': () => import('./pages/api/ApiPage'),
  '/my/api-antara-muka': () => import('./pages/api/ApiPage'),
  '/es/api-interfaz': () => import('./pages/api/ApiPage'),
  // B2B API pages
  '/api/writing-tools': () => import('./pages/api/ApiWritingToolsPage'),
  '/api/chatbot-middleware': () => import('./pages/api/ApiChatbotMiddlewarePage'),
  '/api/content-platforms': () => import('./pages/api/ApiContentPlatformsPage'),
};

// CLS FIX: Pattern-based route matching for dynamic routes like /blog/:slug
// Returns chunk loader if path matches pattern, null otherwise
const ROUTE_PATTERNS: Array<{ pattern: RegExp; loader: () => Promise<unknown> }> = [
  // Blog post routes: /blog/*, /en/blog/*, /de/blog/*, /pt-br/blog/*, /my/blog/*, /es/blog/*, /fr/blog/*, /ja/blog/*, /id/blog/*
  { pattern: /^\/(?:en\/|de\/|pt-br\/|my\/|es\/|fr\/|ja\/|id\/)?blog\/[^/]+$/, loader: () => import('./blog/BlogPost') },
  // Blog list routes
  { pattern: /^\/(?:en\/|de\/|pt-br\/|my\/|es\/|fr\/|ja\/|id\/)?blog\/?$/, loader: () => import('./blog/BlogList') },
  // Blog category routes: /blog/category/*, /de/blog/kategorie/*, etc.
  { pattern: /^\/(?:en\/)?blog\/category\/[^/]+$/, loader: () => import('./blog/CategoryPage') },
  { pattern: /^\/de\/blog\/kategorie\/[^/]+$/, loader: () => import('./blog/CategoryPage') },
  { pattern: /^\/pt-br\/blog\/categoria\/[^/]+$/, loader: () => import('./blog/CategoryPage') },
  { pattern: /^\/my\/blog\/kategori\/[^/]+$/, loader: () => import('./blog/CategoryPage') },
  { pattern: /^\/es\/blog\/categoria\/[^/]+$/, loader: () => import('./blog/CategoryPage') },
  // Blog tag routes
  { pattern: /^\/(?:en\/)?blog\/tag\/[^/]+$/, loader: () => import('./blog/TagPage') },
  { pattern: /^\/de\/blog\/tag\/[^/]+$/, loader: () => import('./blog/TagPage') },
  { pattern: /^\/pt-br\/blog\/tag\/[^/]+$/, loader: () => import('./blog/TagPage') },
  { pattern: /^\/my\/blog\/tag\/[^/]+$/, loader: () => import('./blog/TagPage') },
  { pattern: /^\/es\/blog\/etiqueta\/[^/]+$/, loader: () => import('./blog/TagPage') },
];

// Preload route chunk to prevent hydration mismatch CLS
// Without this, React.lazy() shows Suspense fallback during hydration,
// causing mismatch with SSR HTML → re-render → CLS
async function preloadRouteChunk(): Promise<void> {
  const path = window.location.pathname;

  // First check exact match in ROUTE_CHUNK_MAP
  let chunkLoader = ROUTE_CHUNK_MAP[path];

  // If no exact match, check pattern-based routes (for dynamic routes like /blog/:slug)
  if (!chunkLoader) {
    const matchedPattern = ROUTE_PATTERNS.find(({ pattern }) => pattern.test(path));
    if (matchedPattern) {
      chunkLoader = matchedPattern.loader;
    }
  }

  if (chunkLoader) {
    try {
      console.log('[Hydration] Preloading route chunk for:', path);
      // Race against timeout to prevent blocking hydration forever
      await Promise.race([
        chunkLoader(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Chunk load timeout')), 5000)
        )
      ]);
      console.log('[Hydration] Route chunk loaded');
    } catch (error) {
      // Don't block hydration if chunk fails - app will still work
      // (may have CLS, but better than not loading at all)
      console.warn('[Hydration] Failed to preload chunk, continuing:', error);
    }
  }
}

// AuthProvider loads normally to maintain SSR hydration compatibility
// Performance is optimized via:
// 1. Removed auth loading spinner (Phase 2)
// 2. Auth state loads in parallel with render
// 3. Content shows immediately without waiting for auth

// Use hydration for faster LCP when SSR content is available
const hydrateApp = async () => {
  // CLS FIX: Wait for route chunk before hydrating
  await preloadRouteChunk();
  console.log('[Hydration] Starting client-side hydration');
  try {
    const rootElement = document.getElementById('root')!;

    // Check if SSR content exists (server-rendered HTML)
    const hasSSRContent = rootElement.childNodes.length > 0;

    if (hasSSRContent) {
      // Hydrate existing SSR content for faster LCP
      console.log('[Hydration] SSR content detected, using hydrateRoot');
      ReactDOM.hydrateRoot(
        rootElement,
        <React.StrictMode>
          <BrowserRouter>
            <ThemeProvider>
              <LocaleProvider>
                <AuthProvider>
                  <App />
                </AuthProvider>
              </LocaleProvider>
            </ThemeProvider>
          </BrowserRouter>
        </React.StrictMode>
      );
    } else {
      // Fallback to client-side render if no SSR content
      console.log('[Hydration] No SSR content, using createRoot');
      const root = ReactDOM.createRoot(rootElement);
      root.render(
        <React.StrictMode>
          <BrowserRouter>
            <ThemeProvider>
              <LocaleProvider>
                <AuthProvider>
                  <App />
                </AuthProvider>
              </LocaleProvider>
            </ThemeProvider>
          </BrowserRouter>
        </React.StrictMode>
      );
    }
    console.log('[Hydration] Client-side hydration complete');
  } catch (error) {
    console.error('[Hydration] Error during hydration:', error);
  }
};

if (document.readyState === 'loading') {
  console.log('[Hydration] Document loading, waiting for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', hydrateApp);
} else {
  console.log('[Hydration] Document ready, hydrating immediately');
  hydrateApp();
}

// Service Worker disabled - causing too much TBT (480ms)
// Unregister any existing service workers from previous deployments
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
      console.log('[SW] Unregistered existing service worker');
    }
  });
}

